import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import {
  getAdminCustomerEventImpactingEventsTableDataRetrieveQueryKey,
  getAdminCustomerEventTransactionTableDataRetrieveQueryKey,
  getCustomerEventRetrieveQueryKey,
  useCustomerEventRetrieveSuspense,
} from "gen/endpoints/customer-event/customer-event";
import { getCustomerEventAdminTableDataRetrieveQueryKey } from "gen/endpoints/customer-event-admin/customer-event-admin";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventDelete,
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FeatureFlagNameEnum } from "gen/model/featureFlagNameEnum";
import { FundFamilyStateMachineFieldsEnum } from "gen/model/fundFamilyStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageTabs, PageTabsStatus } from "shared/components/layout/PageTabs";
import { ActionConfirmationDialog } from "shared/components/modal/ActionConfirmationDialog";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export type FundFamilyEventDeleteArgs = Parameters<
  ReturnType<typeof useFundFamilyEventDelete>["mutateAsync"]
>[0];

export type FundFamilyEventFinalizeArgs = Parameters<
  ReturnType<typeof useFundFamilyEventPendingToCompleteUpdate>["mutateAsync"]
>[0];

export const EventPage: React.FC = () => {
  const { t } = useTranslation(["common", "admin", "capitalActivity", "enums"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { customerEventId } = useRequiredStringParams();

  const { mutateAsync: deleteFundFamilyEvent } = useFundFamilyEventDelete();
  const { mutateAsync: completeFundFamilyEvent } =
    useFundFamilyEventPendingToCompleteUpdate();

  const { data: customerEvent } =
    useCustomerEventRetrieveSuspense(customerEventId);

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieve(
    customerEvent.fund_family_id!,
    customerEvent.fund_family_event_id!,
    {
      query: {
        enabled:
          !!customerEvent.fund_family_id &&
          !!customerEvent.fund_family_event_id,
      },
    },
  );

  const invalidateOnRefresh = useCallback(
    () =>
      invalidateQueryKeys(
        queryClient,
        getCustomerEventRetrieveQueryKey(customerEventId),
        getFundFamilyEventRetrieveQueryKey(
          customerEvent.fund_family_id!,
          customerEvent.fund_family_event_id!,
        ),
        getCustomerEventAdminTableDataRetrieveQueryKey(),
        getAdminCustomerEventImpactingEventsTableDataRetrieveQueryKey(
          customerEventId,
        ),
        getAdminCustomerEventTransactionTableDataRetrieveQueryKey(
          customerEventId,
        ),
      ),
    [customerEventId, customerEvent, queryClient],
  );

  const refreshEventActive = useFeatureFlagActive(
    FeatureFlagNameEnum.refresh_out_of_date_events,
  );
  const isServiceUser = useHasPermission(PermissionTypeEnum.service);
  const eventsTablePath = rootRoute({}).admin({}).events({}).$;

  const confirmEventDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<FundFamilyEventDeleteArgs>,
    {
      title: t("events:deleteEventDialog.title"),
      description: t("events:deleteEventDialog.description"),
      successMessage: t("events:deleteEventDialog.success"),
      performDelete: async () => {
        await deleteFundFamilyEvent({
          fundFamilyId: customerEvent.fund_family_id!,
          fundFamilyEventId: customerEvent.fund_family_event_id!,
        });
        navigate(eventsTablePath);
      },
    },
  );

  const confirmEventFinalize = useConfirmationDialog(
    ActionConfirmationDialog<FundFamilyEventFinalizeArgs>,
    {
      title: t("events:finalizeEventDialog.title"),
      description: t("events:finalizeEventDialog.description"),
      successMessage: t("events:finalizeEventDialog.success"),
      performAction: async ({ fundFamilyId, fundFamilyEventId }) => {
        await completeFundFamilyEvent({
          fundFamilyId,
          fundFamilyEventId,
        });
        await invalidateOnRefresh();
      },
    },
  );

  const hiddenTabs: PageTabsStatus = {
    eventDetailsTab: false,
    eventCreateDataTab: !refreshEventActive || !isServiceUser,
  };

  return (
    <PageContent>
      <PageHeader
        title={
          customerEvent.subtype
            ? `${customerEvent.type} - ${customerEvent.subtype}`
            : customerEvent.type
        }
        actions={
          <Restricted permission={PermissionTypeEnum.service}>
            {refreshEventActive && fundFamilyEvent && (
              <PageMenu>
                {fundFamilyEvent.state ===
                  FundFamilyStateMachineFieldsEnum.draft && (
                  <MenuItem
                    onClick={() =>
                      confirmEventFinalize({
                        fundFamilyId: fundFamilyEvent.fund_family_id,
                        fundFamilyEventId: fundFamilyEvent.id!,
                      })
                    }
                  >
                    {t("common:finalize")}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() =>
                    confirmEventDelete({
                      fundFamilyId: fundFamilyEvent.fund_family_id,
                      fundFamilyEventId: fundFamilyEvent.id!,
                    })
                  }
                >
                  {t("common:discard")}
                </MenuItem>
              </PageMenu>
            )}
          </Restricted>
        }
        helperText={
          customerEvent.effective_date && customerEvent.posted_date
            ? t("admin:adminEventPage.helperText", {
                effective_date: formatIsoDateToDisplay(
                  customerEvent.effective_date,
                ),
                posted_date: formatIsoDateToDisplay(customerEvent.posted_date),
              })
            : undefined
        }
        badge={
          customerEvent.state
            ? getCapitalActivityEventStateBadge(customerEvent.state, t)
            : undefined
        }
        backButton={{
          title: t("common:back"),
          path: eventsTablePath,
        }}
      />
      <PageBody>
        <PageTabs context={{}} hiddenTabs={hiddenTabs} />
      </PageBody>
    </PageContent>
  );
};
